import { useMaterialsQuery } from '@/app/hooks/useMaterialsQuery'
import { SearchInput } from '@/view/components/SearchInput'
import { Button } from '@/view/components/ui/button'
import { Dialog } from '@/view/components/ui/dialog'
import { AddCircle, CalendarEdit } from 'iconsax-react'
import { useMemo, useState } from 'react'
import { useCreateMaterialDialog } from '../providers/CreateMaterialDialogProvider'
import { MaterialForm } from './components/MaterialForm'
import { MaterialGroup } from './components/MaterialGroup'
import { getFormattedWeekDate, getLabel, groupMaterialsByWeek } from './helper'
import { useDebounce } from '@/app/hooks/useDebounce'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/view/components/ui/popover'
import { Calendar } from '@/view/components/ui/calendar'
import { ptBR } from 'date-fns/locale'
import { endOfDay, startOfDay, subDays } from 'date-fns'
import { DateRange } from 'react-day-picker'

export const MaterialPage = () => {
  const { isCreateModalOpen, setIsCreateModalOpen } = useCreateMaterialDialog()
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(500, searchTerm)

  const [open, setOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date(),
  })

  const { data } = useMaterialsQuery({
    search: debouncedSearchTerm ?? '',
    startDate: selectedDate?.from?.toISOString(),
    endDate: selectedDate?.to?.toISOString(),
  })

  const onSelect = async (d?: DateRange) => {
    if (!d) return

    setSelectedDate({
      from: d?.from ? startOfDay(d.from) : d?.from,
      to: d?.to ? endOfDay(d.to) : d?.to,
    })

    await new Promise((resolve) => setTimeout(resolve, 200))
    setOpen(false)
  }

  const materialsByWeek = useMemo(
    () => groupMaterialsByWeek(data?.materials ?? []),
    [data],
  )

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true)
  }

  return (
    <>
      <div className="my-5 flex w-full justify-between">
        <div className="flex gap-4">
          <SearchInput
            onChange={handleInputChange}
            value={searchTerm}
            placeholder="Pesquisar conteúdo"
            className="w-[241px]"
          />
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger>
              <div className="flex cursor-pointer items-center gap-2 rounded-lg border-[1px] border-gray01 px-3 py-2">
                <p className="text-xs font-semibold">
                  {getLabel(selectedDate)}
                </p>
                <CalendarEdit size={20} />
              </div>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[346px] border bg-background p-0 shadow-lg"
            >
              <Calendar
                className="flex justify-center"
                initialFocus
                mode="range"
                defaultMonth={new Date()}
                locale={ptBR}
                selected={selectedDate}
                onSelect={(d) => onSelect(d)}
              />
            </PopoverContent>
          </Popover>
        </div>

        <Button
          className="rounded-full bg-primary text-white"
          onClick={handleOpenCreateModal}
        >
          <AddCircle className="mr-2" />
          Nova postagem
        </Button>
      </div>

      {Object.entries(materialsByWeek)
        .sort((a, b) => +b[0] - +a[0])
        .map(([week, materials]) => (
          <MaterialGroup
            key={week}
            label={
              <h2 className="text-lg font-medium ">
                Semana de{' '}
                <span className="font-bold">
                  {getFormattedWeekDate(+week, 'start')}
                </span>
                {' à '}
                <span className="font-bold">
                  {getFormattedWeekDate(+week, 'end')}
                </span>
              </h2>
            }
            materials={materials}
          />
        ))}

      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <MaterialForm />
      </Dialog>
    </>
  )
}
